import { http } from '@noor/api';
import { env } from '@noor/config';
import type { UpsertImageItem, UploadedImage } from '../model/media';
import { upsertImageItemResultSchema } from '../model/media';
import {
  CATEGORY_ICON_IMAGE_TYPE,
  CATEGORY_ICON_SELECTOR,
  EATS_UPLOAD_BUCKET,
  RESTAURANT_CATEGORY_IMAGE_ENTITY,
  type BusinessUploadFolder,
} from '../config/media';
import { listItems, normalizePresignSlots } from './envelope';

const eatsBase = { baseURL: env.VITE_EATS_API_URL };

export class ImageUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageUploadError';
  }
}

export const businessMediaApi = {
  async upsertBannerImages(
    bannerId: string,
    items: { locale: string; link: string }[],
  ): Promise<void> {
    if (items.length === 0) return;
    await http.put<unknown>(
      `/api/v1/admin/images/banner/${bannerId}`,
      { items },
      eatsBase,
    );
  },

  async removeBannerImage(bannerId: string, locale: string): Promise<void> {
    await http.delete<unknown>(
      `/api/v1/admin/images/banner/${bannerId}/${locale}`,
      eatsBase,
    );
  },

  async uploadImages(
    files: File[],
    folder: BusinessUploadFolder,
  ): Promise<UploadedImage[]> {
    if (files.length === 0) return [];
    const raw = await http.post<unknown>(
      '/api/v1/admin/uploads/presign',
      { count: files.length, bucket: EATS_UPLOAD_BUCKET, folder },
      eatsBase,
    );
    const slots = normalizePresignSlots(raw);
    const paired = files.map((file, index) => {
      const slot = slots[index];
      if (!slot) throw new ImageUploadError(`No upload slot for file ${index + 1}`);
      if (slot.error) throw new ImageUploadError(slot.error);
      if (!slot.upload_url || !slot.key || !slot.public_url)
        throw new ImageUploadError(`Upload slot ${index + 1} is incomplete`);
      return {
        file,
        uploadUrl: slot.upload_url,
        key: slot.key,
        publicUrl: slot.public_url,
      };
    });
    await Promise.all(
      paired.map(({ file, uploadUrl }) => businessMediaApi.putFile(uploadUrl, file)),
    );
    return paired.map(({ key, publicUrl }) => ({ key, publicUrl }));
  },

  async putFile(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!response.ok) {
      throw new ImageUploadError(`Upload failed: ${response.status}`);
    }
  },

  async upsertCategoryIcon(categoryId: string, key: string): Promise<boolean> {
    const item: UpsertImageItem = {
      image_type: CATEGORY_ICON_IMAGE_TYPE,
      link: key,
    };
    const raw = await http.put<unknown>(
      `/api/v1/admin/images/${RESTAURANT_CATEGORY_IMAGE_ENTITY}/${categoryId}`,
      { items: [item] },
      eatsBase,
    );
    // The spec answers `{ data: {}, list: [...] }` — the results are not under `data`.
    const first = listItems(raw)[0];
    const parsed = upsertImageItemResultSchema.safeParse(first ?? {});
    return parsed.success && parsed.data.pending_change != null;
  },

  async removeCategoryIcon(categoryId: string): Promise<void> {
    await http.delete<unknown>(
      `/api/v1/admin/images/${RESTAURANT_CATEGORY_IMAGE_ENTITY}/${categoryId}/${CATEGORY_ICON_SELECTOR}`,
      eatsBase,
    );
  },
};
